import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { DocPanel } from '../../components/DocPanel';
import { Download, Edit, Maximize2, PenLine, Save, X } from '../../icons';
import { Button } from '../../components/shared';
import { formatBytes, patchFile, type NodePublic } from '../../api/workspace';
import { apiClient } from '../../api/client';
import { requestPresignedDownload } from '../../api/files';
import {
  previewDocOfNode, previewKindOfNode, isEditableKind, workspaceOpenKindOfNode,
} from '../../lib/workspace-preview';
import { fetchFileBytesShared, invalidateFileBytes } from '../../lib/file-bytes-cache';
import { currentColorMode, subscribeColorMode } from '../../lib/app-color-mode';
import { WorkspaceHwpPreview } from './WorkspaceHwpPreview';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';
import { getSharedLang, subscribeSharedLang } from '../../lib/base-i18n';
import { formatWorkspaceDateTime } from './workspace-datetime';
import { kindOfMime } from '../../lib/file-kind';
import { layerAlert, layerConfirm, layerPrompt } from '../../components/LayerPopup/LayerPopup';
import { useAuthStore } from '../../store/authStore';
import { WorkspaceFileIcon } from './WorkspaceFileIcon';
import { WorkspaceImagePreview } from './WorkspaceImagePreview';
import { WorkspaceHtmlPreview } from './WorkspaceHtmlPreview';
import { WorkspaceMediaPreview } from './WorkspaceMediaPreview';
// 전체화면의 좌측 썸네일 레일 — **채팅과 같은 컴포넌트**를 그대로 쓴다(수정 0).
import { PreviewThumbRail } from '../ChatPage/ChatArea/preview/PreviewThumbRail';
import { WorkspaceTextEditor, type WorkspaceTextEditorHandle } from './WorkspaceTextEditor';
import type { OfficeAiDock, OfficeAiFallback, OfficeEditorHandle, OfficeHost } from '@workchat/office';
import { startOfficeAiTurn, cancelOfficeAiTurn, type OfficeAiTurnRequest } from '../../api/office-ai';
import { WorkspaceDocAi } from './DocAi/WorkspaceDocAi';
import type { WorkspaceDocAiTarget } from './DocAi/WorkspaceDocAiChat';
import { resolveRenameName } from './DocAi/rename-name';

// 경량 렌더러(티어 1) — 채팅 미리보기와 **같은 컴포넌트**를 재사용한다(수정 0).
const DocxPreview = lazy(() => import('../ChatPage/ChatArea/preview/DocxPreview'));
const XlsxPreview = lazy(() => import('../ChatPage/ChatArea/preview/XlsxPreview'));
const PptxPreview = lazy(() => import('../ChatPage/ChatArea/preview/PptxPreview'));
const PdfPreview = lazy(() => import('../ChatPage/ChatArea/preview/PdfPreview'));

/**
 * 텍스트로 열 수 있는 최대 크기 (2026-08-14).
 *
 * 원문을 통째로 받아 DOM 에 붙이는 구조라 상한이 필요하다 — 100MB 로그를 열면 탭이 멈춘다.
 * 넘으면 다운로드로 안내한다(조용히 빈 화면을 주지 않는다).
 */
const TEXT_MAX_BYTES = 2 * 1024 * 1024;

/**
 * 호스트 폴백 AI 카드에 실어 보내는 문서 글자 수 상한 (2026-08-15, PR3).
 *
 * 서버 프롬프트 예산은 110,000 **바이트**이고 한글은 3바이트/자다
 * (`apps/chat-server/src/modules/ai/office-ai-encode.ts`). 지시문·대화 기록 몫을 빼고 잡는다 —
 * 넘치면 서버 사다리가 한 번 더 자르지만, 여기서 먼저 줄여야 헛되이 올려 보내지 않는다.
 */
const AI_CONTEXT_MAX_CHARS = 24_000;

/** 1차 요약의 날짜 — 목록과 같은 규칙(초는 뜻이 없어 분까지). */
function formatWhen(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return formatWorkspaceDateTime(d);
}

/**
 * 워크스페이스 문서 패널 (2026-08-12) — 2계층.
 *
 *   티어 1: 경량 미리보기. 파일을 누르면 **즉시** 뜬다. 기존 채팅 미리보기 렌더러 그대로.
 *   티어 2: GenOffice 편집기. `편집` 을 눌렀을 때만 지연 로드(약 510KB gzip).
 *
 * 티어를 나눈 이유는 무게다. 편집기를 처음부터 받게 하면 파일 한 번 훑어보는 데 수백 KB 를
 * 내려받게 된다. 채팅 패널은 이 파일을 건드리지 않는다(회귀 0).
 *
 * ⚠ 새 창·iframe 을 만들지 않는다. 같은 문서·같은 origin·같은 세션 위에서 마운트한다.
 */
export function WorkspaceDocPanel({
  node, onClose, onSaved, initialExpanded = false, readOnly = false,
}: {
  node: NodePublic;
  /**
   * 읽기 전용으로 연다 (2026-10-28 사용자 지시 「보기 권한인데 편집 버튼을 누르면 편집이 된다」).
   * 이 값이 없어도 **올린 사람이 아니면** 편집·이름 바꾸기를 감춘다 — 서버의 덮어쓰기(presigned-replace)가
   * 올린 사람만 받기 때문이다. 회사 문서처럼 화면이 따로 아는 경우에 켠다.
   */
  readOnly?: boolean;
  onClose: () => void;
  /** 저장 후 목록 갱신(크기 변경 반영). */
  onSaved?: () => void;
  /**
   * 열자마자 전체화면으로 (2026-08-14).
   *
   * 목록의 미리보기(◉) 아이콘은 "이 파일을 보겠다" 는 뜻이라, 300px 짜리 우측 패널에
   * 문서를 밀어 넣는 것은 답이 아니다(표도 그만큼 좁아진다). 눌렀으면 전체화면으로 연다.
   */
  initialExpanded?: boolean;
}) {
  const kind = previewKindOfNode(node);
  const doc = previewDocOfNode(node);
  /** 경량 렌더러가 없는 형식까지 포함한 판정 — 텍스트·이미지가 여기서만 늘어난다. */
  const openKind = workspaceOpenKindOfNode(node);
  /**
   * 고칠 수 있는 사람인가 — 공유받은 파일(보기·편집 모두)은 서버가 저장을 404 로 막는다.
   * 로그인 정보를 아직 모르면 예전처럼 둔다(판정은 어차피 서버가 한다).
   */
  const myId = useAuthStore((st) => st.user?.id);
  const canWrite = !readOnly && (myId == null || String(node.uploadedBy) === String(myId));
  const editable = canWrite && isEditableKind(kind);
  /** 평문 텍스트는 오피스 번들 없이 textarea 로 고친다(단, 사람이 다룰 크기까지만). */
  const textEditable = canWrite && openKind === 'text' && node.sizeBytes <= TEXT_MAX_BYTES;
  /**
   * 페이지를 여러 장 나열하는 형식인가 — 썸네일 레일이 뜻을 갖는 조건.
   *
   * 채팅의 `isPagedKind`(ChatDocPreviewPanel)는 pdf·pptx 둘이다. 여기에 **hwp 를 더한다**
   * (2026-08-14 2차) — 한글 미리보기는 쪽마다 SVG 를 그려 두므로 레일이 그대로 성립한다.
   *
   * ⚠ docx 는 **아직 아니다.** 그 렌더러는 `breakPages: false`·`ignoreHeight: true` 로
   *   **쪽을 나누지 않고** 패널 폭에 흐르게 그린다(채팅과 공유하는 설정이다). 즉 가리킬
   *   '쪽' 자체가 없어서, 레일을 붙이려면 먼저 그 렌더링을 쪽 단위로 바꿔야 하고 그러면
   *   Word 미리보기의 생김새가 통째로 달라진다 — 눈으로 확인하고 결정할 일이라 뒤로 미룬다.
   */
  const pagedKind = kind === 'pdf' || kind === 'pptx' || kind === 'hwp';

  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [expanded, setExpanded] = useState(initialExpanded);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);
  /**
   * 편집기가 오래 걸리는 일을 하는 동안의 안내(내보내기 등). 이 작업들은 메인 스레드를
   * 붙잡아서, 표시가 없으면 사용자에게는 **앱이 멈춘 것과 구별되지 않는다**.
   */
  const [progress, setProgress] = useState<string | null>(null);

  // 콜백 ref 로 대입하므로 쓰기 가능한 형태여야 한다(`RefObject` 는 current 가 읽기 전용).
  const editorSlotRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<OfficeEditorHandle | null>(null);
  /**
   * 편집기 내장 AI 카드의 여닫이 (2026-08-15, PR2).
   *
   * ⚠ ref 가 아니라 **state** 여야 한다 — 편집기 마운트는 비동기(지연 import)라
   *   ref 에만 넣으면 그 시점에 다시 그려지지 않아 AI 버튼이 영영 안 나타난다.
   */
  const [aiDock, setAiDock] = useState<OfficeAiDock | null>(null);
  /**
   * 편집기 안에 AI 가 **없을 때**(한글) 호스트 폴백 카드가 문서를 읽는 창구 (2026-08-15, PR3).
   * `aiDock` 과 배타적이다 — 둘 다 오면 카드가 둘 뜬다.
   */
  const [aiFallback, setAiFallback] = useState<OfficeAiFallback | null>(null);
  /**
   * 편집 슬롯의 실제 DOM — 폴백 카드를 여기로 포털한다 (PR3).
   *
   * ⚠ ref 만으로는 안 된다. 슬롯은 편집 모드에서야 생기고 ref 대입은 리렌더를 부르지
   *   않아, 카드가 영영 안 붙는다. 콜백 ref 로 **state 에도** 넣어 붙는 순간 다시 그린다.
   */
  const [editorSlotEl, setEditorSlotEl] = useState<HTMLDivElement | null>(null);
  const adoptSlot = useCallback((el: HTMLDivElement | null) => {
    editorSlotRef.current = el;
    setEditorSlotEl(el);
  }, []);
  const textHandleRef = useRef<WorkspaceTextEditorHandle | null>(null);
  // ⚠ onSaved 를 마운트 effect 의 deps 에 넣으면 안 된다. 부모가 인라인 화살표 함수로
  //   넘기면 렌더마다 identity 가 바뀌어 **편집기가 통째로 재마운트**되고, 편집 중이던
  //   내용과 커서가 사라진다(브라우저 검증에서 실제 재마운트 확인). ref 로 최신 값만 들고 간다.
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  /**
   * 화면에 쓰는 파일 이름.
   *
   * ⚠ `node` 는 **부모가 목록에서 떠 준 스냅샷**이라, 이름을 바꿔도 부모가 다시 그려 주기
   *   전까지 `node.name` 은 옛 이름이다(AI 가 이름을 바꿨는데 제목이 그대로였던 이유).
   *   그래서 바꾼 이름을 여기서 먼저 들고 있는다. 부모가 따라오면(= `node.name` 이 바뀌면)
   *   아래 effect 가 이 임시값을 버려 두 곳이 다시 하나가 된다.
   */
  const [nameOverride, setNameOverride] = useState<string | null>(null);
  const displayName = nameOverride ?? node.name;
  useEffect(() => { setNameOverride(null); }, [node.id, node.name]);

  const [renaming, setRenaming] = useState(false);
  const [renameBusy, setRenameBusy] = useState(false);
  const renameInputRef = useRef<HTMLInputElement | null>(null);

  // 파일이 바뀌면 편집 중이던 이름 입력은 닫는다 — 다른 파일에 옛 이름을 쓰면 안 된다.
  useEffect(() => { setRenaming(false); }, [node.id]);

  useEffect(() => {
    if (!renaming) return;
    const el = renameInputRef.current;
    if (!el) return;
    el.focus();
    // 확장자를 뺀 부분만 선택 — 사람이 고치려는 곳은 거의 항상 그쪽이다.
    const dot = el.value.lastIndexOf('.');
    el.setSelectionRange(0, dot > 0 ? dot : el.value.length);
  }, [renaming]);

  /**
   * 제목에서 이름 바꾸기.
   *
   * 목록의 이름 바꾸기(`useWorkspaceActions.rename`)와 **같은 규칙**을 쓴다 — 전체 이름을
   * 그대로 고치고 확장자를 강제하지 않는다. 사람이 보고 고치는 자리라 AI 도구(`rename_file`,
   * 확장자 고정)와 규칙이 다른 것이 맞다.
   */
  const commitRename = useCallback(async (raw: string): Promise<void> => {
    const next = raw.trim();
    setRenaming(false);
    if (!next || next === displayName) return;
    setRenameBusy(true);
    // 낙관적 반영 — 서버 왕복 동안 제목이 옛 이름으로 남아 있으면 눌린 것 같지 않다.
    setNameOverride(next);
    try {
      const updated = await patchFile(node.id, { name: next });
      setNameOverride(updated.name);
      onSavedRef.current?.();
    } catch (err) {
      setNameOverride(null); // 되돌린다 — 서버가 거절했는데 화면만 바뀌어 있으면 안 된다.
      void layerAlert(`${tWorkspace('workspace.editor.rename_failed')}: ${(err as Error)?.message ?? ''}`);
    } finally {
      setRenameBusy(false);
    }
  }, [displayName, node.id]);

  /**
   * **AI 가 파일 이름을 바꾸는 창구 — 일곱 편집기 공통** (2026-08-15).
   *
   * 세 곳이 이것을 쓴다: 평문 카드의 `rename_file` 도구 · 한글 카드의 같은 도구 ·
   * GenOffice 다섯의 상류 루프에 끼워 넣는 공통 스킬(`OfficeHost.renameFile`).
   * 한 곳에 모은 이유는 **이름을 아는 곳이 셋**이기 때문이다 — 워크스페이스 목록, 이 패널의
   * 제목, 편집기의 저장 대상. 여기서 한 번 바꿔야 셋이 같이 따라온다.
   *
   * ⚠ 규칙 위반은 **throw** 다. 도구가 사유를 그대로 모델에게 되돌려 다른 이름을 고르게
   *   하려면 사유가 문장으로 살아 있어야 한다(`false` 를 돌려주면 이유가 사라진다).
   */
  const renameForAi = useCallback(async (requested: string): Promise<string> => {
    // 지금 이름 = 화면에 떠 있는 이름. 한 번 바꾼 뒤에는 `node.name` 이 아직 옛 이름일 수 있다.
    const now = nameOverride ?? node.name;
    const checked = resolveRenameName(now, requested);
    if (!checked.ok) throw new Error(checked.reason);
    // 같은 이름이면 서버를 부르지 않는다 — 도구가 "이미 그 이름" 으로 답한다.
    if (checked.name === now) return now;

    const updated = await patchFile(node.id, { name: checked.name });
    // ⚠ 제목은 **여기서 직접** 갱신한다. `node` 는 부모가 떠 준 스냅샷이라 onSaved 로 목록을
    //   새로 고쳐도 이 패널의 prop 이 곧바로 따라오지 않는다(AI 가 이름을 바꿨는데 편집 화면
    //   제목만 옛 이름이던 원인).
    setNameOverride(updated.name);
    onSavedRef.current?.();
    return updated.name;
  }, [nameOverride, node.id, node.name]);

  /**
   * ⚠ 마운트 effect 안에서 만든 `host` 는 그 시점의 클로저를 붙든다. ref 로 들고 가야
   *   이름을 두 번 바꿀 때도 최신 함수를 부른다(편집기는 다시 마운트되지 않는다).
   */
  const renameForAiRef = useRef(renameForAi);
  renameForAiRef.current = renameForAi;

  // 파일이 바뀌면 항상 미리보기부터 다시 시작한다 — 편집 중이던 상태가 다른 파일로 새면 안 된다.
  useEffect(() => {
    setMode('preview');
    setDirty(false);
    setEditorError(null);
    // 열린 채로 다른 파일을 미리보기하면 그 파일도 전체화면이어야 한다 — 여는 방법이
    // 같은데 결과가 다르면(첫 파일만 전체화면) 규칙으로 읽히지 않는다.
    setExpanded(initialExpanded);
  }, [node.id, initialExpanded]);

  /** 티어 2 마운트 — `편집` 을 눌렀을 때만 번들을 받는다. */
  useEffect(() => {
    if (mode !== 'edit') return;
    // ⚠ 오피스 편집기는 **경량 렌더러가 있는 형식**에만 마운트한다. 텍스트(.txt/.log/…)는
    //   kind 가 null 이라 이 가드가 없으면 아래 폴백이 마크다운 편집기를 띄운다 —
    //   평문을 WYSIWYG 로 왕복시켜 조용히 내용을 바꾸게 된다.
    if (kind === null) return;
    const slot = editorSlotRef.current;
    if (!slot) return;

    let disposed = false;
    setEditorError(null);

    /**
     * 마운트 결과를 받아 든다 — 여섯 갈래가 똑같이 하던 대입을 한 곳으로 모은다.
     * AI 여닫이는 편집기마다 있을 수도(GenOffice 5종) 없을 수도(한글) 있다.
     */
    const adopt = (h: OfficeEditorHandle) => {
      handleRef.current = h;
      setAiDock(h.ai ?? null);
      setAiFallback(h.aiFallback ?? null);
    };

    const host: OfficeHost = {
      file: {
        fileId: node.id, name: node.name, mimeType: node.mimeType ?? null,
        // "다른 이름으로 저장" 이 같은 폴더에 새 파일을 만들 수 있도록.
        parentId: node.parentId ?? null,
      },
      async api(method, path, body) {
        const res = await apiClient.request({ method, url: path, data: body });
        return res.data;
      },
      getLang: () => getSharedLang(),
      subscribeLang: (fn) => subscribeSharedLang(fn),
      /**
       * 편집기의 밝게/어둡게는 **앱을 따른다** (2026-09-02).
       *
       * 예전에는 `'system'` 을 줬는데, 그러면 편집기가 자기 스코프의 `data-theme` 을
       * **지우고** CSS 의 `@media (prefers-color-scheme: dark)` 갈래로 떨어진다 — 즉
       * **OS 설정**을 본다. 앱이 밝게인데 OS 가 어둡게인 사람에게는 흰 앱 안에서
       * 편집기만 새까맣게 떴다(운영 실측: 앱 배경 밝기 244 vs 편집기 37). 여섯 편집기가
       * 모두 이 한 줄을 쓰므로 증상도 여섯 곳에서 똑같았다.
       */
      getTheme: () => currentColorMode(),
      subscribeTheme: (fn) => subscribeColorMode(() => fn()),
      /**
       * 티어1 미리보기가 방금 받아 둔 바이트를 편집기가 그대로 쓴다 — 같은 파일을 두 번
       * 받지 않기 위해서다(26MB PDF 에서 52MB → 26MB). 캐시에 없으면 여기서 받고
       * 그 결과가 다시 캐시에 남아 다음 열기에 재사용된다.
       */
      readBytes: () => fetchFileBytesShared(node.id),
      // PowerPoint 주석 등 **문서에 사람 이름이 남는 자리**에 쓴다. 데스크톱 원본은 OS
      // 계정명을 쓰던 자리라 브라우저에서는 로그인 사용자 이름을 준다(없으면 편집기가
      // 기본값으로 대신한다 — 이름이 없다고 주석을 막지는 않는다).
      getUserName: () => useAuthStore.getState().user?.name,
      /**
       * "다른 이름으로 저장" · "내보내기" 가 이름을 묻는 자리. 데스크톱 원본은 OS 저장
       * 대화상자를 띄우던 곳인데, 브라우저에는 "이름을 정해 저장" 대화상자가 없다
       * (`<input type="file">` 은 열기 전용). 그래서 앱 레이어 팝업으로 묻는다.
       */
      promptFileName: (defaultName, purpose) => layerPrompt(
        tWorkspace(purpose === 'export' ? 'workspace.editor.export_name' : 'workspace.editor.save_as_name'),
        { defaultValue: defaultName, placeholder: defaultName },
      ),
      /**
       * 되돌릴 수 없는 편집 전 확인. 데스크톱 원본이 OS 경고 대화상자를 띄우던 자리라
       * 앱 레이어 팝업으로 대신한다(가져온 차트를 처음 고치면 세부 서식이 사라진다).
       * 문구는 편집기가 준다 — 무엇을 잃는지는 편집기만 안다.
       */
      confirm: (title, detail) => layerConfirm(detail, { title, variant: 'warn' }),
      /**
       * 편집기 안의 AI 가 파일 이름을 바꾸는 창구 (2026-08-15).
       *
       * 이 한 줄이 GenOffice 다섯(md·Word·PDF·Excel·PPT)에 `rename_file` 을 켠다 —
       * `packages/office` 의 파사드가 상류 스킬 옆에 공통 도구로 붙인다. 없으면 도구가
       * 통째로 빠지므로, 창구를 안 주는 편집기에서는 종전과 똑같이 "못 한다" 고 답한다.
       */
      renameFile: (name) => renameForAiRef.current(name),
      /**
       * 편집기 안의 AI 코파일럿이 모델을 부르는 통로 (2026-08-14).
       *
       * 편집기 패키지가 아니라 **호스트**가 준다 — 편집기 번들이 다섯 벌이라 전송
       * 클라이언트를 안에 두면 다섯 번 실려 서로 어긋나고, 한글·평문 편집기용 패널이
       * 같은 경로를 못 쓴다. `host.api`(axios, 30초 타임아웃)로는 한 턴을 못 태워서
       * 전용 fetch 클라이언트를 쓴다.
       */
      aiStream: (req, cb) => startOfficeAiTurn(req as OfficeAiTurnRequest, cb),
      aiStreamCancel: (requestId) => cancelOfficeAiTurn(requestId),
      onProgress: setProgress,
      onDirtyChange: setDirty,
      onSaved: (info) => {
        setDirty(false);
        // ⚠ 저장으로 파일 내용이 바뀌었다 — 공유 캐시를 버리지 않으면 다시 열 때
        //   **저장 전 바이트**가 그대로 나온다(미리보기·편집기 양쪽 모두).
        invalidateFileBytes(node.id);
        if (info.fileId && info.fileId !== node.id) invalidateFileBytes(info.fileId);
        onSavedRef.current?.();
        // "다른 이름으로 저장" 은 **새 워크스페이스 파일**을 만든다. 편집기는 그 순간부터 새
        // 파일을 덮어쓰지만 이 패널의 제목은 원본 그대로라, 알리지 않으면 어느 파일을 고치고
        // 있는지 알 수 없다. (패널 선택을 새 파일로 옮기는 것은 후속 — 목록 갱신은 위에서 했다.)
        if (info.switched) {
          void layerAlert(tWorkspace('workspace.editor.saved_as_new', { name: info.name ?? '' }));
        }
      },
      onError: (message) => { void layerAlert(message); },
    };

    void (async () => {
      try {
        // 지연 로드 — 이 import 들이 편집기 청크(JS+CSS)를 받아온다.
        // ⚠ CSS 를 함께 가져와야 한다. vite lib 빌드는 CSS 를 별도 파일로 뽑고 JS 안에서
        //   참조하지 않으므로, 소비자가 명시적으로 import 하지 않으면 편집기가 **스타일 없이**
        //   뜬다(빌드는 성공해서 조용히 지나간다). 동적 import 라 청크에 같이 분리된다.
        // ⚠ 형식별로 **번들이 다르다**. .md 하나 여는 데 Word 렌더러까지 받지 않도록
        //   `@workchat/office`(markdown) 와 `@workchat/office/docx` 를 나눠 두었다.
        if (kind === 'docx') {
          const [{ mountDocsEditor }] = await Promise.all([
            import('@workchat/office/docx'),
            import('@workchat/office/docx.css'),
          ]);
          if (disposed) return;
          adopt(mountDocsEditor(slot, host));
          return;
        }
        if (kind === 'pdf') {
          const [{ mountPdfEditor }] = await Promise.all([
            import('@workchat/office/pdf'),
            import('@workchat/office/pdf.css'),
          ]);
          if (disposed) return;
          adopt(mountPdfEditor(slot, host));
          return;
        }
        if (kind === 'xlsx') {
          const [{ mountXlsxEditor }] = await Promise.all([
            import('@workchat/office/xlsx'),
            import('@workchat/office/xlsx.css'),
          ]);
          if (disposed) return;
          adopt(mountXlsxEditor(slot, host));
          return;
        }
        if (kind === 'pptx') {
          const [{ mountSlidesEditor }] = await Promise.all([
            import('@workchat/office/pptx'),
            import('@workchat/office/pptx.css'),
          ]);
          if (disposed) return;
          adopt(mountSlidesEditor(slot, host));
          return;
        }
        if (kind === 'hwp') {
          const [{ mountHwpEditor }] = await Promise.all([
            import('@workchat/office/hwp'),
            import('@workchat/office/hwp.css'),
          ]);
          if (disposed) return;
          adopt(mountHwpEditor(slot, host));
          return;
        }
        const [{ mountEditor }] = await Promise.all([
          import('@workchat/office'),
          import('@workchat/office/style.css'),
        ]);
        if (disposed) return;
        adopt(mountEditor(slot, { kind: 'markdown', host }));
      } catch (err) {
        if (!disposed) {
          setEditorError((err as Error)?.message ?? tWorkspace('workspace.editor.load_failed'));
          setMode('preview');
        }
      }
    })();

    return () => {
      disposed = true;
      handleRef.current?.destroy();
      handleRef.current = null;
      // 편집기가 사라졌으므로 AI 여닫이도 함께 버린다 — 남겨 두면 이미 없는 DOM 을
      // 가리키는 핸들에 대고 버튼을 그리게 된다(파일 전환에서 실제로 걸린다).
      setAiDock(null);
      // 폴백 창구도 같은 이유로 버린다 — 남으면 이미 닫힌 문서를 읽으러 간다.
      setAiFallback(null);
    };
    // onSaved 는 의도적으로 deps 에서 제외 — 위 onSavedRef 주석 참조.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, kind, node.id, node.name, node.mimeType, node.parentId]);

  /**
   * 호스트 폴백 AI 카드가 문서에 닿는 창구 (2026-08-15, PR3).
   *
   * 두 갈래를 여기서 하나로 좁힌다 — 한글은 편집기가 준 `aiFallback`(wasm 읽기),
   * 평문은 우리 `<textarea>` 핸들이다. 카드는 어느 쪽인지 몰라도 된다.
   *
   * ⚠ 함수는 **호출 시점에** ref 를 읽는다. 로드 전에 굳혀 두면 사용자가 방금 고친 문장이
   *   아니라 빈 문서를 보고 답한다.
   */
  const aiTarget = useMemo<WorkspaceDocAiTarget | null>(() => {
    if (textEditable) {
      return {
        editorKind: 'text',
        fileId: node.id,
        // ⚠ 바꾼 이름이 곧바로 반영돼야 한다 — 도구가 "지금 이름" 을 여기서 읽어 확장자를
        //   지키고 "이미 그 이름" 을 판정한다. `node.name` 은 한 박자 늦는다.
        fileName: displayName,
        getText: () => textHandleRef.current?.getText() ?? null,
        insertText: (text) => textHandleRef.current?.insertText(text) ?? false,
        // ★ 이 하나가 카드를 도구 모드로 바꾼다 — AI 가 평문을 직접 고친다 (Phase B).
        setText: (text) => textHandleRef.current?.setText(text) ?? false,
        renameFile: renameForAi,
      };
    }
    if (aiFallback) {
      return {
        editorKind: aiFallback.editorKind,
        fileId: node.id,
        fileName: displayName,
        getText: () => aiFallback.getText(AI_CONTEXT_MAX_CHARS),
        // 이름 바꾸기는 **본문과 무관**하므로 일곱 편집기 공통이다 (2026-08-15).
        renameFile: renameForAi,
        /**
         * ★ 본문 편집 (Phase C) — 도구를 **편집기가** 준다.
         *
         * 평문처럼 글 전체를 갈아끼울 수 없어서다(문단·글자모양·개체가 얽혀 있다).
         * 호스트는 도구의 속을 모르고 루프에 실어 보내기만 한다.
         */
        ...(aiFallback.getEditTools ? { getTools: () => aiFallback.getEditTools!() } : {}),
        // 되돌리기도 편집기 몫이다 — 이전 전문을 들고 있을 수 없으니 자기 스택을 푼다.
        ...(aiFallback.undo ? { undo: (steps: number) => aiFallback.undo!(steps) } : {}),
      };
    }
    return null;
  }, [textEditable, aiFallback, node.id, displayName, renameForAi]);

  const handleSave = useCallback(async () => {
    // 텍스트는 오피스 핸들이 없다 — 저장 버튼 하나가 두 편집기를 부른다.
    const handle = textEditable ? textHandleRef.current : handleRef.current;
    if (!handle) return;
    setSaving(true);
    try {
      await handle.save();
    } finally {
      setSaving(false);
    }
  }, [textEditable]);

  /** 미저장 변경이 있으면 되묻는다 — 편집기를 내리면 내용이 사라진다. */
  const guardLeave = useCallback(async (): Promise<boolean> => {
    if (mode !== 'edit' || !dirty) return true;
    return layerConfirm(tWorkspace('workspace.editor.confirm_discard'));
  }, [mode, dirty]);

  const handleClose = useCallback(() => {
    void (async () => { if (await guardLeave()) onClose(); })();
  }, [guardLeave, onClose]);

  async function handleDownload() {
    try {
      const res = await requestPresignedDownload(node.id);
      // ⚠ window.open 금지(설치형에서 팝업 차단·별창 분리) — 앵커 다운로드로 처리한다.
      const a = document.createElement('a');
      a.href = res.downloadUrl;
      a.download = node.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      void layerAlert(`${tWorkspace('workspace.editor.download_failed')}: ${(err as Error)?.message ?? ''}`);
    }
  }

  const title = (
    <span className="workspace-docpanel-title">
      <WorkspaceFileIcon node={node} size={16} />
      {renaming ? (
        <input
          ref={renameInputRef}
          className="workspace-docpanel-nameinput"
          defaultValue={displayName}
          aria-label={tWorkspace('workspace.row.rename_prompt')}
          disabled={renameBusy}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); void commitRename(e.currentTarget.value); }
            // Esc 는 여기서 멈춘다 — 위로 새면 문서 패널이 통째로 닫힌다.
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setRenaming(false); }
          }}
          onBlur={(e) => void commitRename(e.currentTarget.value)}
        />
      ) : (
        <>
          <span className="workspace-docpanel-name" title={displayName}>{displayName}</span>
          {canWrite && <button
            type="button"
            className="workspace-docpanel-rename"
            aria-label={tWorkspace('workspace.editor.rename_title')}
            title={tWorkspace('workspace.editor.rename_title')}
            onClick={() => setRenaming(true)}
          >
            <PenLine size={12} aria-hidden />
          </button>}
        </>
      )}
      {dirty && <span className="workspace-docpanel-dirty" aria-label={tWorkspace('workspace.editor.unsaved')}>•</span>}
    </span>
  );

  /**
   * 툴바 — 패널 **헤더 한 줄**로 올라간다 (2026-08-14).
   *
   * 예전에는 본문 첫 줄에 따로 그렸다. 헤더(파일명·전체화면·닫기) 아래에 툴바가 한 줄 더
   * 붙는 구조라 전체화면에서 특히 세로가 아깝고, 본문을 스크롤하면 툴바가 함께 밀려 올라가
   * **저장 버튼이 화면 밖으로 사라졌다**. 헤더는 스크롤 밖이라 항상 남는다.
   */
  const toolbar = (
    <>
      <span className="workspace-docpanel-meta">{formatBytes(node.sizeBytes)}</span>
      {(editable || textEditable) && mode === 'preview' && (
        <Button
          variant="primary"
          size="compact"
          onClick={() => {
            // 오피스 편집기는 진입 시 자동 확장 — 기본 패널 폭(약 300px)에서는 레이아웃이
            // 뭉개져 실질적으로 못 쓴다(브라우저 검증에서 확인). 사용자는 ⤢ 로 되돌릴 수 있다.
            // 텍스트는 textarea 하나라 좁은 폭에서도 멀쩡하다 — 화면을 뺏지 않는다.
            if (!textEditable) setExpanded(true);
            setMode('edit');
          }}
          iconLeft={<Edit size={13} aria-hidden />}
        >
          {tWorkspace('workspace.editor.edit')}
        </Button>
      )}
      {mode === 'edit' && (
        <>
          <Button variant="primary" size="compact"
            disabled={saving || !dirty} onClick={() => void handleSave()}
            iconLeft={<Save size={13} aria-hidden />}
          >
            {saving ? tWorkspace('workspace.editor.saving') : tWorkspace('workspace.editor.save')}
          </Button>
          <Button variant="secondary" size="compact"
            onClick={() => void (async () => { if (await guardLeave()) setMode('preview'); })()}
            iconLeft={<X size={13} aria-hidden />}
          >
            {tWorkspace('workspace.editor.close_editor')}
          </Button>
        </>
      )}
      <Button variant="secondary" size="compact"
        onClick={() => void handleDownload()}
        iconLeft={<Download size={13} aria-hidden />}
      >
        {tWorkspace('workspace.editor.download')}
      </Button>
    </>
  );

  /** 지금 레일을 붙이는가 — 채팅과 같은 조건(전체화면 + 페이지 형식 + 미리보기 모드). */
  const railed = expanded && mode === 'preview' && pagedKind;

  return (
    <div
      className={`workspace-docpanel${expanded ? ' workspace-docpanel--expanded' : ''}${mode === 'edit' ? ' workspace-docpanel--editing' : ''}${railed ? ' workspace-docpanel--railed' : ''}`}
      /*
       * AI 카드의 제목 (2026-08-15, PR2).
       *
       * 상류 패널의 제목은 **전 언어에서 리터럴 `Genspark`** 라 CSS 로 덮어야 하는데,
       * office CSS 는 빌드 산출물이라 우리 i18n 을 읽을 수 없다. 그래서 값을 여기서
       * 커스텀 프로퍼티로 흘려보낸다 — 커스텀 프로퍼티는 `.go-root` 경계를 그냥 통과해
       * 상속되므로 스코프 접두와 무관하게 닿는다.
       * ⚠ `content:` 가 받아야 하므로 **따옴표째** 넣는다(JSON.stringify 가 이스케이프까지 한다).
       */
      style={{ '--wsai-title': JSON.stringify(tWorkspace('workspace.ai.title')) } as CSSProperties}
    >
      <DocPanel
        title={title}
        onExpand={() => setExpanded((v) => !v)}
        expanded={expanded}
        onClose={handleClose}
        /*
         * 편집 모드에서는 Esc 로 패널을 닫지 않는다 (2026-08-18).
         *
         * 편집기 안의 Esc 는 **편집기 것**이다 — 찾기·글자 모양·문자표 같은 대화상자와
         * 메뉴 드롭다운을 닫는 표준 키다. 그런데 상류 핸들러는 전파를 멈추지 않고, 이
         * 리스너는 document 에서 듣기 때문에 대화상자를 닫으려고 누른 Esc 가 그대로
         * 올라와 **문서 패널을 통째로 닫아** 버렸다(미저장이 아닐 때).
         * 닫기는 헤더의 "편집 닫기" 버튼이라는 분명한 경로가 이미 있다.
         */
        closeOnEsc={!dirty && mode !== 'edit'}
        actions={toolbar}
      >
        {/*
          1차(인라인) 요약 — 사용자 요구(2026-08-14): "미리보기를 누르면 기존의 세부정보에서
          보이던 것처럼 1차로 간단히 보이고 전체 확대키를 누르면 전체가 보이도록".

          좁은 패널에서 문서만 덜렁 그리면 무슨 파일인지(형식·크기·수정일)를 알 수 없어
          세부정보를 다시 열게 된다. 전체화면에서는 감춘다 — 그때는 문서 자체가 주인공이고
          같은 값이 헤더에도 있다.
        */}
        {!expanded && (
          <div className="workspace-docpanel-brief">
            <dl className="workspace-docpanel-kv">
              <dt>{tWorkspace('workspace.detail.format')}</dt>
              <dd>{tWorkspace(`workspace.kind.${kindOfMime(node.mimeType)}` as WorkspaceI18nKey)}</dd>
              <dt>{tWorkspace('workspace.col.size')}</dt>
              <dd className="ws-num">{formatBytes(node.sizeBytes)}</dd>
              <dt>{tWorkspace('workspace.col.modified')}</dt>
              <dd className="ws-num">{formatWhen(node.updatedAt ?? node.createdAt)}</dd>
            </dl>
            <Button variant="secondary" size="compact" className="workspace-docpanel-full"
              onClick={() => setExpanded(true)}
              iconLeft={<Maximize2 size={13} aria-hidden />}
            >
              {tWorkspace('workspace.editor.open_full')}
            </Button>
          </div>
        )}

        {editorError && (
          <div className="workspace-modal-error" role="alert">{editorError}</div>
        )}

        {/* 편집 모드에서도 미리보기 DOM 을 지우지 않고 감춘다 — 편집을 닫았을 때
            바이트를 다시 받지 않아도 되고, 렌더러의 재초기화 비용도 없다. */}
        <div className="workspace-docpanel-main" hidden={mode === 'edit'}>
          {/*
            좌측 썸네일 레일 (2026-08-14 2차) — 채팅 전체보기의 그것과 **같은 컴포넌트**다.
            (이름: PreviewThumbRail / 페이지·슬라이드 썸네일 레일, chat-doc-preview-rail)

            레일은 스스로 아무것도 그리지 않는다. 페이지를 그리는 주체(PdfPreview·PptxPreview)가
            "몇 장인지 · 썸네일을 어떻게 그리는지 · 어떻게 이동하는지" 를 preview-nav 레지스트리에
            등록하고 레일이 그걸 구독한다. 워크스페이스는 **그 렌더러들을 이미 그대로 재사용**
            하고 있어서, 레일을 여기 한 줄 얹는 것만으로 등록이 살아난다(렌더러 수정 0).

            조건은 채팅과 같다 — **전체화면 + 페이지가 여러 장인 형식**. 좁은 우측 패널에서는
            레일이 본문을 잡아먹기만 한다. 장수가 1이면 레일 스스로 null 을 낸다.
          */}
          {railed && <PreviewThumbRail />}
          <div className="workspace-docpanel-body">
          {!doc && !openKind && (
            <div className="workspace-docpanel-unsupported">
              {tWorkspace('workspace.editor.unsupported')}
            </div>
          )}
          {!doc && openKind === 'image' && (
            <WorkspaceImagePreview fileId={node.id} name={node.name} />
          )}
          {!doc && (openKind === 'video' || openKind === 'audio') && (
            <WorkspaceMediaPreview fileId={node.id} name={node.name} kind={openKind} />
          )}
          {/* 큰 로그·CSV 를 통째로 받아 DOM 에 붙이면 탭이 멈춘다 — 경계를 넘으면 안 연다. */}
          {!doc && openKind === 'text' && (
            node.sizeBytes > TEXT_MAX_BYTES ? (
              <div className="workspace-docpanel-unsupported">
                {tWorkspace('workspace.editor.text_too_large', { size: formatBytes(TEXT_MAX_BYTES) })}
              </div>
            ) : (
              <MarkdownTextPreview fileId={node.id} />
            )
          )}
          {doc && (
            <Suspense fallback={<div className="workspace-docpanel-status" role="status">{tWorkspace('workspace.grid.loading')}</div>}>
              {doc.kind === 'docx' && <DocxPreview doc={doc} />}
              {doc.kind === 'xlsx' && <XlsxPreview doc={doc} />}
              {doc.kind === 'pptx' && <PptxPreview doc={doc} />}
              {doc.kind === 'pdf' && <PdfPreview doc={doc} />}
              {doc.kind === 'markdown' && <MarkdownTextPreview fileId={node.id} />}
              {/*
                html 은 **화면으로 그린다** (2026-08-14 2차 사용자 요구). 예전에는 마크다운과
                같은 갈래로 묶어 원문을 그대로 뿌렸는데, 채팅에서는 이미 격리 iframe 으로
                그리고 있어 같은 파일이 화면마다 다르게 보였다. 격리 규약은 채팅과 동일하다.
              */}
              {doc.kind === 'html' && (
                <WorkspaceHtmlPreview fileId={node.id} name={node.name} />
              )}
              {/*
                한글 문서도 이제 **읽기 전용으로 그린다**(`@workchat/office/hwp-preview`, 234KB).
                전에는 경량 렌더러가 없어 "편집을 누르세요" 안내만 뒀는데, 그리는 데 필요한 것은
                엔진뿐이라(`pageCount`+`renderPageSvg`) 스튜디오 셸 없이 성립한다.

                편집 버튼은 **그대로 둔다** — 미리보기는 쪽 수 상한이 있고 고칠 수도 없다.
              */}
              {doc.kind === 'hwp' && (
                <div className="workspace-docpanel-hwp-open">
                  <WorkspaceHwpPreview fileId={node.id} />
                  {canWrite && <div className="workspace-docpanel-hwp-actions">
                    <span>{tWorkspace('workspace.editor.hwp_open_edit')}</span>
                    <Button variant="secondary" size="compact"
                      onClick={() => { setExpanded(true); setMode('edit'); }}
                      iconLeft={<Edit size={13} />}
                    >
                      {tWorkspace('workspace.editor.edit')}
                    </Button>
                  </div>}
                </div>
              )}
            </Suspense>
          )}
          </div>
        </div>

        {/*
          편집기 슬롯이 둘이다 — 오피스(빈 div 에 mount) 와 텍스트(React 컴포넌트).
          한 슬롯을 공유하면 mount 가 React 가 그린 DOM 을 지우거나 그 반대가 된다.
        */}
        {textEditable ? (
          mode === 'edit' && (
            // 폴백 AI 카드가 이 상자를 기준으로 뜬다(포털 대상) — 오피스 슬롯과 같은 ref 다.
            <div className="workspace-docpanel-editor" ref={adoptSlot}>
              <WorkspaceTextEditor
                ref={textHandleRef}
                fileId={node.id}
                onDirtyChange={setDirty}
                onSaved={() => onSavedRef.current?.()}
                onError={(message) => { void layerAlert(message); }}
              />
            </div>
          )
        ) : (
          <div className="workspace-docpanel-editor" ref={adoptSlot} hidden={mode !== 'edit'} />
        )}
        {/*
          ⚠ 편집기 슬롯을 **덮되 지우지 않는다** — 편집기는 계속 살아 있고 그 위에 안내만
            얹는다. 슬롯을 언마운트하면 진행 중인 작업이 통째로 날아간다.
        */}
        {progress && (
          <div className="workspace-docpanel-progress" role="status" aria-live="polite">
            <span className="workspace-docpanel-progress-spinner" aria-hidden="true" />
            <span>{progress}</span>
          </div>
        )}
      </DocPanel>
      {/*
        플로팅 AI 버튼 (2026-08-15, PR2) — `<DocPanel>` 의 **형제**다.

        자식(본문)으로 넣으면 `.doc-panel__body` 의 `overflow:auto` 에 딸려 스크롤되고
        헤더를 덮지 못한다. 형제로 두면 `.workspace-docpanel` 이 그대로 기준 상자가 된다 —
        인라인일 때 `position:relative`, 전체화면일 때 `position:fixed` 가 이미 있어
        두 상태에서 같은 코드가 옳다(진행 오버레이가 쓰는 그 전례).

        편집 모드에서만 그린다. 미리보기에는 고칠 대상이 없어 AI 도 할 일이 없고,
        편집기가 마운트되기 전에는 여닫이(`aiDock`) 자체가 없다.

        PR3: 한글·평문은 상류 카드가 없어 `target` 으로 간다 — 같은 버튼이 호스트 폴백
        카드를 편집 슬롯(`slot`) 안에 그린다. 둘은 배타적이고, 버튼은 문자 그대로 같다.
      */}
      {mode === 'edit' && <WorkspaceDocAi dock={aiDock} target={aiTarget} slot={editorSlotEl} />}
    </div>
  );
}

/**
 * 마크다운/HTML 원문 미리보기 (티어 1).
 *
 * 채팅 쪽은 마크다운을 메시지 렌더러로 그리지만 여기서는 원문 텍스트만 보여준다 —
 * 어차피 `편집` 한 번이면 진짜 편집기가 뜨고, 이 단계에서 마크다운 파서를 또 받을 이유가 없다.
 */
function MarkdownTextPreview({ fileId }: { fileId: string }) {
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setText(null);
    setError(null);
    void (async () => {
      try {
        const res = await requestPresignedDownload(fileId, { purpose: 'preview' }); // 마크다운 미리보기 = 「열람」(§3.4.1)
        const r = await fetch(res.downloadUrl);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const t = await r.text();
        if (!cancelled) setText(t);
      } catch (err) {
        if (!cancelled) setError((err as Error)?.message ?? 'error');
      }
    })();
    return () => { cancelled = true; };
  }, [fileId]);

  if (error) return <div className="workspace-docpanel-status" role="status">{error}</div>;
  if (text === null) return <div className="workspace-docpanel-status" role="status">{tWorkspace('workspace.grid.loading')}</div>;
  return <pre className="workspace-docpanel-text">{text}</pre>;
}
